/**
 * Main application component with routing and auth guards.
 *
 * Routes are split into two trees:
 * - /admin/* -- Admin dashboard (existing D1 pages)
 * - /crm/*         -- Agent CRM (D2 pages)
 *
 * All page-level components are lazy-loaded via React.lazy() to enable
 * code splitting. Each route chunk is fetched on demand, reducing the
 * initial bundle size.
 *
 * REQ D1-AUTH-004: Frontend routes for admin dashboard reject agents.
 */
import React, { Suspense } from "react";
import * as Sentry from "@sentry/react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { BrandingProvider } from "./contexts/BrandingContext";
import { ModulesProvider } from "./contexts/ModulesContext";
import { TourProvider } from "./contexts/TourContext";
import { SupportChatProvider } from "./contexts/SupportChatContext";
import { PreviewPersonaProvider } from "./contexts/PreviewPersonaContext";
import { WelcomePrompt } from "./components/tours/WelcomePrompt";
import { IdleSignOut } from "./components/auth/IdleSignOut";
import { SectionNudge } from "./components/tours/SectionNudge";
import { ToastProvider } from "./components/ui/Toast";
import {
  ProtectedRoute,
  AuthenticatedRoute,
  FinanceDeskExcludedRoute,
  AdminRoute,
  RoleRedirect,
  AccountsRoute,
  FinanceRoute,
  FinanceAccountsRoute,
  FinanceFicaOutstandingRoute,
  FinanceDealsLodgedRoute,
  PayslipsRoute,
  CommissionSheetRoute,
  OverriderScheduleRoute,
  OutstandingDealsRoute,
  MarketShareRoute,
  BondAnalyticsRoute,
  ManagerRoute,
  ParalegalRoute,
  BehindScheduleRoute,
  ConveyancerRoute,
  PartnersRoute,
  TemplatesRoute,
  ManageUsersRoute,
  AgentComplianceRoute,
  HRTemplatesRoute,
  ManagementDashboardsRoute,
  FarmBookImportRoute,
  CommissionRoute,
  SalesTargetsRoute,
  AgentDashboardRoute,
  CanvasserExcludedRoute,
  OfficeAdminExcludedRoute,
} from "./components/auth/ProtectedRoute";
import { AdminLayout } from "./components/layout/AdminLayout";
import { CrmLayout } from "./components/layout/CrmLayout";
import BrandedLockup from "./components/BrandedLockup";
import RouteErrorBoundary from "./components/RouteErrorBoundary";
import { ReleaseUpdatePrompt } from "./components/ReleaseUpdatePrompt";

// -- Lazy-loaded page components (code-split per route) --

// Public
const Login = React.lazy(() => import("./pages/Login"));
const ForgotPassword = React.lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = React.lazy(() => import("./pages/ResetPassword"));
const SetPasswordRequired = React.lazy(() => import("./pages/SetPasswordRequired"));
const AcceptTermsRequired = React.lazy(() => import("./pages/AcceptTermsRequired"));
const SelfServicePage = React.lazy(() => import("./pages/SelfServicePage"));
// Public, no-login marketing/legal pages (#2015) -- About + POPIA privacy
// policy, reachable WITHOUT authentication so an AWS SES reviewer can verify
// a real business (the SES Website URL must not point at the login wall).
const About = React.lazy(() => import("./pages/public/About"));
const Privacy = React.lazy(() => import("./pages/public/Privacy"));

// Admin dashboard pages (D1)
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const MonthlyDeals = React.lazy(() => import("./pages/MonthlyDeals"));
const AttorneyPipeline = React.lazy(() => import("./pages/AttorneyPipeline"));
const Commissions = React.lazy(() => import("./pages/Commissions"));
const CommissionSheet = React.lazy(() => import("./pages/admin/CommissionSheet"));
const OutstandingDeals = React.lazy(() => import("./pages/admin/OutstandingDeals"));
// #3720: Leases register -- every live lease + its renewal lifecycle.
const LeasesRegister = React.lazy(() => import("./pages/crm/LeasesRegister"));
const Payslips = React.lazy(() => import("./pages/admin/Payslips"));
const DealPipeline = React.lazy(() => import("./pages/dashboard/DealPipeline"));
const PipelineCashflow = React.lazy(() => import("./pages/admin/PipelineCashflow"));
const SalesTargets = React.lazy(() => import("./pages/SalesTargets"));
const MarketShare = React.lazy(() => import("./pages/MarketShare"));
const BondAnalytics = React.lazy(() => import("./pages/admin/BondAnalytics"));
const ListingToRegistration = React.lazy(() => import("./pages/admin/ListingToRegistration"));
const ActivityMetrics = React.lazy(() => import("./pages/ActivityMetrics"));
const AgentUsage = React.lazy(() => import("./pages/AgentUsage"));
const AgentProfile = React.lazy(
  () => import("./pages/crm/oversight/AgentProfile"),
);
// #2823: the manager oversight MASTER deal queue -- one filterable view of the
// outstanding book, replacing several separately-labelled fixed buckets.
const OversightDealQueue = React.lazy(
  () => import("./pages/crm/oversight/OversightDealQueue"),
);
const Leaderboard = React.lazy(() => import("./pages/Leaderboard"));
const UserManagement = React.lazy(() => import("./pages/admin/UserManagement"));
const PrivilegedAuthQueue = React.lazy(
  () => import("./pages/admin/PrivilegedAuthQueue")
);
// PROP Data unmatched-lead review queue (#3475) -- admin/office only.
const PropDataAmbiguousLeads = React.lazy(
  () => import("./pages/admin/PropDataAmbiguousLeads")
);
// Ownerless buyer enquiries awaiting a hand-out (#3474) -- admin/office only.
const UnassignedLeads = React.lazy(
  () => import("./pages/admin/UnassignedLeads")
);
const ContactDeduplication = React.lazy(
  () => import("./pages/admin/ContactDeduplication")
);
const DealDeduplication = React.lazy(
  () => import("./pages/admin/DealDeduplication")
);
const AgentComplianceRoster = React.lazy(
  () => import("./pages/admin/compliance/AgentComplianceRoster")
);
// DataReconciliation page hidden (#371) — pipeline auto-resolves conflicts; component kept, route removed.
const FicaDashboard = React.lazy(() => import("./pages/admin/FicaDashboard"));
const STRQueue = React.lazy(() => import("./pages/admin/STRQueue"));
const BatchVerification = React.lazy(() => import("./pages/admin/BatchVerification"));
// Onboarding wizard (admin-only, first-run) — #516
const OnboardingWizard = React.lazy(() => import("./pages/admin/onboarding/OnboardingWizard"));
const MyBusiness = React.lazy(() => import("./pages/admin/business/MyBusiness"));
const FormattedDataGuide = React.lazy(
  () => import("./pages/admin/guides/FormattedDataGuide")
);
const UnformattedDataGuide = React.lazy(
  () => import("./pages/admin/guides/UnformattedDataGuide")
);

// CRM pages (D2)
const CrmDashboard = React.lazy(() => import("./pages/crm/CrmDashboard"));
const ContactList = React.lazy(() => import("./pages/crm/contacts/ContactList"));
const ContactDetail = React.lazy(() => import("./pages/crm/contacts/ContactDetail"));
const FarmList = React.lazy(() => import("./pages/crm/farm/FarmList"));
const FarmDetail = React.lazy(() => import("./pages/crm/farm/FarmDetail"));
const Referrals = React.lazy(() => import("./pages/crm/farm/Referrals"));
const Canvassing = React.lazy(() => import("./pages/crm/farm/Canvassing"));
const PropertyLookup = React.lazy(
  () => import("./pages/crm/prospecting/PropertyLookup"),
);
const ListingList = React.lazy(() => import("./pages/crm/listings/ListingList"));
const ListingDetail = React.lazy(() => import("./pages/crm/listings/ListingDetail"));
// Propose-and-confirm PropData farmbook erf-match review queue (#2877).
const FarmLinkSuggestions = React.lazy(
  () => import("./pages/crm/listings/FarmLinkSuggestions"),
);
const AddMandate = React.lazy(() => import("./pages/crm/listings/AddMandate"));
const BuyerList = React.lazy(() => import("./pages/crm/buyers/BuyerList"));
const BuyerDetail = React.lazy(() => import("./pages/crm/buyers/BuyerDetail"));
const DealList = React.lazy(() => import("./pages/crm/deals/DealList"));
const DealDetail = React.lazy(() => import("./pages/crm/deals/DealDetail"));
const AddSale = React.lazy(() => import("./pages/crm/deals/AddSale"));
const FicaMatrix = React.lazy(() => import("./pages/crm/fica/FicaMatrix"));
const ConveyancerList = React.lazy(() => import("./pages/crm/conveyancers/ConveyancerList"));
// Partner directory (D9-EXT-001 / D9-EXT-009, WP20) -- outside firms deals are
// shared with, their people and partner links.
const PartnersList = React.lazy(() => import("./pages/admin/partners/PartnersList"));
const PartnerDetail = React.lazy(() => import("./pages/admin/partners/PartnerDetail"));
const DocumentLibrary = React.lazy(() => import("./pages/crm/documents/DocumentLibrary"));
const DocumentVault = React.lazy(() => import("./pages/crm/documentvault/DocumentVault"));
const HRTemplates = React.lazy(() => import("./pages/crm/documentvault/HRTemplates"));
const TrainingVault = React.lazy(() => import("./pages/crm/training/TrainingVault"));
const TaskList = React.lazy(() => import("./pages/crm/tasks/TaskList"));
const TaskDetail = React.lazy(() => import("./pages/crm/tasks/TaskDetail"));
const AppointmentsAgenda = React.lazy(
  () => import("./pages/crm/appointments/AppointmentsAgenda")
);
const MessagingPage = React.lazy(() => import("./pages/crm/messaging/MessagingPage"));
const MyCommission = React.lazy(() => import("./pages/crm/stats/MyCommission"));
const MyTargets = React.lazy(() => import("./pages/crm/stats/MyTargets"));
const MyLeaderboard = React.lazy(() => import("./pages/crm/stats/MyLeaderboard"));
const KPIActivityLogPage = React.lazy(() => import("./pages/crm/kpis/KPIActivityLog"));
const KPIConfirmation = React.lazy(() => import("./pages/crm/kpis/KPIConfirmation"));
const PersonalSettings = React.lazy(
  () => import("./pages/crm/settings/PersonalSettings"),
);
const MyDocuments = React.lazy(
  () => import("./pages/crm/compliance/MyDocuments"),
);
const ConnectAI = React.lazy(() => import("./pages/crm/connect/ConnectAI"));
const OAuthConsent = React.lazy(() => import("./pages/oauth/OAuthConsent"));
// Deal-finance spine (#452): gated landing shells + maker-checker surfaces.
const LegalQueue = React.lazy(() => import("./pages/crm/legal/LegalQueue"));
const ParalegalWorkspace = React.lazy(
  () => import("./pages/crm/legal/ParalegalWorkspace"),
);
const FileCoverPage = React.lazy(() => import("./pages/crm/legal/FileCoverPage"));
const BehindScheduleView = React.lazy(
  () => import("./pages/crm/legal/BehindScheduleView")
);
const FinanceQueue = React.lazy(() => import("./pages/crm/finance/FinanceQueue"));
const ManagerSignoffQueue = React.lazy(
  () => import("./pages/crm/manage/ManagerSignoffQueue")
);
const AccountsInvestQueue = React.lazy(
  () => import("./pages/crm/accounts/AccountsInvestQueue")
);
// #1506: combined Finance/Accounts workspace (one person at LWP).
const FinanceAccountsQueue = React.lazy(
  () => import("./pages/crm/financeaccounts/FinanceAccountsQueue")
);
const FinanceFicaOutstanding = React.lazy(
  () => import("./pages/crm/finance/FinanceFicaOutstanding")
);
const FinanceDealsLodged = React.lazy(
  () => import("./pages/crm/finance/FinanceDealsLodged")
);
const CommissionQuestionnaireForm = React.lazy(
  () => import("./pages/crm/deals/CommissionQuestionnaireForm")
);
const CommissionAuthorizationInbox = React.lazy(
  () => import("./pages/admin/CommissionAuthorizationInbox")
);
// Admin Communications — company-wide message history + delivery health (#748)
const AdminCommunications = React.lazy(
  () => import("./pages/admin/Communications")
);
// Feedback reports — running list, role-scoped (#547, D4-FBK-017). Mounted on
// both the admin and CRM trees; one component, role detected via useAuth().
// Admin KPI Reports — company-wide agent KPI performance (#849)
const KPIReport = React.lazy(() => import("./pages/admin/kpis/KPIReport"));
const KPITiers = React.lazy(() => import("./pages/admin/kpis/KPITiers"));
const KPIMetricEditor = React.lazy(() => import("./pages/admin/kpis/KPIMetricEditor"));
const KPIUpload = React.lazy(() => import("./pages/admin/kpis/KPIUpload"));
const KPIExceptions = React.lazy(() => import("./pages/admin/kpis/KPIExceptions"));
const KPIMonthlyScorecard = React.lazy(
  () => import("./pages/admin/kpis/KPIMonthlyScorecard"),
);
const Integrations = React.lazy(() => import("./pages/admin/integrations/Integrations"));
// Usage Analytics — rank-1 (super-admin) only adoption/friction/AI-usage
// dashboard (D8-ANA-W3-*, #3038, epic #3034 WP3).
const AnalyticsDashboard = React.lazy(() => import("./pages/admin/AnalyticsDashboard"));
// Areas master list — admin self-service area management (#1751).
const AreaManagement = React.lazy(() => import("./pages/admin/AreaManagement"));
// Import farm book — self-service farm-book .xlsx upload (#3442).
const FarmBookImport = React.lazy(() => import("./pages/admin/FarmBookImport"));
// Over-rider schedules — admin/finance self-service editing (#2865).
const OverriderSchedules = React.lazy(() => import("./pages/admin/OverriderSchedules"));
const FeedbackReports = React.lazy(() => import("./pages/feedback/FeedbackReports"));
const FeedbackCuration = React.lazy(() => import("./pages/feedback/FeedbackCuration"));
// Morning briefs -- daily feedback brief e-mail history (D4-FBK-038, #3920), admin-only.
const FeedbackBriefs = React.lazy(() => import("./pages/feedback/FeedbackBriefs"));
// Support insights -- help-assistant usage (D10-OPS-001, #3607), admin-only.
const SupportInsights = React.lazy(() => import("./pages/admin/SupportInsights"));
// Mandate/FICA checklist catalogue — admin self-service (#1741)
const ChecklistTemplates = React.lazy(() => import("./pages/admin/ChecklistTemplates"));

// Client portal (D9-POR-011, #3534) -- the client-facing, password-less
// surface. Mounted OUTSIDE the CRM provider tree (see App below).
const PortalPage = React.lazy(() => import("./pages/portal/PortalPage"));
// Roleplayer (attorney / bond originator) page on the same shell (D9-EXT-008, #3546).
const PartnersPage = React.lazy(() => import("./pages/portal/partners/PartnersPage"));
// Buyer intake wizard on the same shell (D9-INT-011, #3542).
const IntakePage = React.lazy(() => import("./pages/portal/intake/IntakePage"));

/**
 * The client portal route. It sits under BrandingProvider only: no
 * AuthProvider (which would try to restore a CRM session from the refresh
 * cookie), no ModulesProvider, tours, toasts or feedback widgets -- a portal
 * visitor is never a CRM user, and the portal talks only to
 * `/api/v1/portal/**` plus the public config endpoints (D9-SEC-012).
 */
function PortalRoute({ surface = "client" }: { surface?: "client" | "partners" | "intake" }) {
  // Its own boundary: the CRM's ErrorFallback redirects auth failures to
  // /login, which is meaningless to a portal visitor.
  return (
    <Sentry.ErrorBoundary
      fallback={
        <p className="p-6 text-center text-base text-[var(--color-text-secondary)]">
          Something went wrong. Please open your link again.
        </p>
      }
    >
      <Suspense fallback={<BrandedLockup />}>
        {surface === "partners" ? (
          <PartnersPage />
        ) : surface === "intake" ? (
          <IntakePage />
        ) : (
          <PortalPage />
        )}
      </Suspense>
    </Sentry.ErrorBoundary>
  );
}

function App() {
  return (
    <BrowserRouter>
      {/* New-release prompt (#4243): offers a reload once a newer build is
          deployed, so an open tab does not keep running old code. */}
      <ReleaseUpdatePrompt />
      <BrandingProvider>
        <Routes>
          <Route path="/portal/*" element={<PortalRoute />} />
          <Route path="/partners/*" element={<PortalRoute surface="partners" />} />
          <Route path="/intake/*" element={<PortalRoute surface="intake" />} />
          <Route path="*" element={<CrmApp />} />
        </Routes>
      </BrandingProvider>
    </BrowserRouter>
  );
}

/** The staff CRM: every route except the client-facing portal. */
function CrmApp() {
  return (
        <ModulesProvider>
          <AuthProvider>
            <ToastProvider>
            <PreviewPersonaProvider>
            <TourProvider>
            {/* Help assistant conversation (D10-UI-003): above the routes so it
                survives every navigation, including a /crm <-> /admin switch.
                Inert until a layout's SupportBubble asks for the status. */}
            <SupportChatProvider>
              {/* First-login guided-tours welcome prompt (#1324, D4-ONB-006).
                  Mounted once here so it covers admin/agent/back-office landings;
                  it self-suppresses unless the user is on their own landing and
                  eligible. */}
              <WelcomePrompt />
              {/* Idle sign-out (D2-SEC-014, #4081): prompts, then signs an
                  unattended session out after the agency's idle timeout. */}
              <IdleSignOut />
              {/* First-visit section nudge (#1329, D4-ONB-010): the first time a
                  user opens a section with a registered mini-tour they have not
                  engaged, offer it via a small dismissible toast. Registry-driven
                  and generic over every persona's surfaces -- no per-page wiring. */}
              <SectionNudge />
              <RouteErrorBoundary>
              <Suspense fallback={<BrandedLockup />}>
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<Login />} />
                {/* OAuth consent screen (#438) — claude.ai/ChatGPT connector
                    approval. Renders for the logged-in user; bounces to /login
                    itself if there is no session. */}
                <Route path="/oauth/consent" element={<OAuthConsent />} />
                {/* Account-claim / password flows (#698) -- public, no auth */}
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/self-service/:token" element={<SelfServicePage />} />

                {/* Public, no-login pages (#2015): About + POPIA privacy policy.
                    These sit OUTSIDE every auth guard -- exactly like /login and
                    the password flows above -- so a hard refresh returns the page
                    and never redirects to /login. They expose no authenticated
                    data; purely public static content for the real sender domain. */}
                <Route path="/about" element={<About />} />
                <Route path="/privacy" element={<Privacy />} />

                {/* Forced first-login set-password (#698) -- authenticated but
                    OUTSIDE the must_reset_password guard to avoid a redirect loop. */}
                <Route element={<AuthenticatedRoute />}>
                  <Route path="/set-password" element={<SetPasswordRequired />} />
                </Route>

                {/* Forced first-login Terms & Conditions acceptance
                    (D8-ANA-W1-009, #3036) -- authenticated but OUTSIDE the
                    must_accept_terms guard to avoid a redirect loop, matching
                    the /set-password pattern above. */}
                <Route element={<AuthenticatedRoute />}>
                  <Route path="/accept-terms" element={<AcceptTermsRequired />} />
                </Route>

                {/* Admin routes (admin role required) */}
                <Route element={<AdminRoute />}>
                  <Route element={<AdminLayout />}>
                    <Route path="/admin/dashboard" element={<Dashboard />} />
                    {/* First-run onboarding wizard (#516) — admin-only */}
                    <Route path="/admin/setup" element={<OnboardingWizard />} />
                    {/* My Business settings hub (#517) — admin-only */}
                    <Route path="/admin/business" element={<MyBusiness />} />
                    {/* Onboarding data-import guides (#553) — in-app, NO GitHub links */}
                    <Route
                      path="/admin/guides/formatted-data"
                      element={<FormattedDataGuide />}
                    />
                    <Route
                      path="/admin/guides/unformatted-data"
                      element={<UnformattedDataGuide />}
                    />
                    <Route path="/admin/monthly-deals" element={<MonthlyDeals />} />
                    <Route path="/admin/commission" element={<Commissions />} />
                    <Route path="/admin/commission-sheet" element={<CommissionSheet />} />
                    <Route path="/admin/outstanding-deals" element={<OutstandingDeals />} />
                    <Route path="/admin/leases" element={<LeasesRegister />} />
                    <Route path="/admin/sales-targets" element={<SalesTargets />} />
                    <Route path="/admin/activity" element={<ActivityMetrics />} />
                    <Route path="/admin/agent-usage" element={<AgentUsage />} />
                    <Route path="/admin/leaderboard" element={<Leaderboard />} />
                    <Route path="/admin/attorney-pipeline" element={<AttorneyPipeline />} />
                    <Route path="/admin/partners" element={<PartnersList />} />
                    <Route path="/admin/partners/:id" element={<PartnerDetail />} />
                    <Route path="/admin/deal-pipeline" element={<DealPipeline />} />
                    <Route path="/admin/pipeline-cashflow" element={<PipelineCashflow />} />
                    <Route path="/admin/market-share" element={<MarketShare />} />
                    <Route path="/admin/bond-analytics" element={<BondAnalytics />} />
                    <Route
                      path="/admin/listing-to-registration"
                      element={<ListingToRegistration />}
                    />
                    <Route path="/admin/users" element={<UserManagement />} />
                    {/* Contact duplicate review queue + merge (#1439, EPIC #608) — admin-only */}
                    {/* Unmatched PROP Data leads awaiting a human call (#3475). */}
                    <Route
                      path="/admin/leads/ambiguous"
                      element={<PropDataAmbiguousLeads />}
                    />
                    {/* Leads that arrived with no agent on them (#3474). */}
                    <Route
                      path="/admin/leads/unassigned"
                      element={<UnassignedLeads />}
                    />
                    <Route
                      path="/admin/contacts/dedup"
                      element={<ContactDeduplication />}
                    />
                    {/* Deal duplicate review queue + confirm-merge (#2173) — admin-only */}
                    <Route
                      path="/admin/deals/dedup"
                      element={<DealDeduplication />}
                    />
                    <Route path="/admin/compliance" element={<FicaDashboard />} />
                    <Route path="/admin/compliance/batch" element={<BatchVerification />} />
                    <Route
                      path="/admin/compliance/agents"
                      element={<AgentComplianceRoster />}
                    />
                    <Route path="/admin/str-queue" element={<STRQueue />} />
                    {/* #4620: the searchable deals list -- admin-layout mount of
                        the shared /crm/deals page (the /admin/leases pattern). */}
                    <Route path="/admin/deals" element={<DealList />} />
                    {/* Deal detail — admin-layout mount so admins stay in the admin panel */}
                    <Route path="/admin/deals/:id" element={<DealDetail />} />
                    {/* Connect your AI — admin-layout copy so admins stay in the admin panel */}
                    <Route path="/admin/connect" element={<ConnectAI />} />
                    {/* Commission Authorization inbox — super-user checker (#452, D4-AUTH-002) */}
                    <Route
                      path="/admin/commission-authorizations"
                      element={<CommissionAuthorizationInbox />}
                    />
                    {/* Super-User-authorization queue — maker-checker for admin
                        creation + sales-target edits (#2271 #2250, EPIC #2317). */}
                    <Route
                      path="/admin/privileged-authorizations"
                      element={<PrivilegedAuthQueue />}
                    />
                    {/* Communications — company-wide message history + delivery health (#748) */}
                    <Route path="/admin/communications" element={<AdminCommunications />} />
                    {/* Feedback reports — running list (#547, D4-FBK-017) */}
                    {/* KPI Reports — company-wide agent KPI performance (#849) */}
                    <Route path="/admin/kpis" element={<KPIReport />} />
                    {/* KPI Tiers & Targets — per-tier configurable targets (D5-KPI-001, #918) */}
                    <Route path="/admin/kpi-tiers" element={<KPITiers />} />
                    {/* KPI Metrics — admin self-service metric catalogue editor (#1498) */}
                    <Route path="/admin/kpi-metrics" element={<KPIMetricEditor />} />
                    {/* KPI Monthly Upload — source-file ingestion + column mapping + staging (#1784) */}
                    <Route path="/admin/kpi-upload" element={<KPIUpload />} />
                    {/* KPI Exceptions / Data Issues report — pre-lock review gate (#1787) */}
                    <Route
                      path="/admin/kpi-exceptions"
                      element={<KPIExceptions />}
                    />
                    {/* KPI Monthly Scorecard & outputs — scorecard, Involved-vs-
                        Share, property-level detail, ranking, rolling average,
                        Excel/PDF export, locked archive (#1788, epic #1783). */}
                    <Route
                      path="/admin/kpi-scorecard"
                      element={<KPIMonthlyScorecard />}
                    />
                    <Route path="/admin/integrations" element={<Integrations />} />
                    {/* Usage Analytics — rank-1 (super-admin) only (D8-ANA-W3-*, #3038) */}
                    <Route path="/admin/analytics" element={<AnalyticsDashboard />} />
                    {/* Mandate/FICA checklist catalogue — admin self-service (#1741) */}
                    <Route path="/admin/checklist-templates" element={<ChecklistTemplates />} />
                    {/* Areas — admin self-service master list management (#1751) */}
                    <Route path="/admin/areas" element={<AreaManagement />} />
                    {/* Import farm book — self-service .xlsx upload (#3442) */}
                    <Route
                      path="/admin/farm-book-import"
                      element={<FarmBookImport />}
                    />
                    {/* Over-rider schedules — admin/finance self-service editing (#2865) */}
                    <Route
                      path="/admin/overrider-schedules"
                      element={<OverriderSchedules />}
                    />
                    {/* Payslips — admin/accounts on-demand generation (#984, D5-PAY-001) */}
                    <Route path="/admin/payslips" element={<Payslips />} />
                    <Route path="/admin/feedback/reports" element={<FeedbackReports />} />
                    {/* Daily curation — approve the day's requested changes in one go */}
                    <Route path="/admin/feedback/curation" element={<FeedbackCuration />} />
                    <Route path="/admin/feedback/curation/:date" element={<FeedbackCuration />} />
                    {/* Morning briefs — the daily feedback brief e-mail history (D4-FBK-038, #3920) */}
                    <Route path="/admin/feedback/briefs" element={<FeedbackBriefs />} />
                    <Route path="/admin/support-insights" element={<SupportInsights />} />
                    {/* Document Vault — company library on its own admin-layout
                        mount so admins never leave the admin shell (#1153,
                        follow-up to #1150). Reuses the shared component, which
                        derives the company-wide upload affordance from useAuth() role. */}
                    <Route path="/admin/document-vault" element={<DocumentVault />} />
                    {/* Restricted HR / Personnel Document Templates (#2813) — on
                        its own admin-layout mount (the #1153 /admin/document-vault
                        precedent). Whole /admin/* shell is already IsAdmin-only,
                        so every admin reaches it here; the CRM-shell mount below
                        is additionally flag-gated for a non-admin (e.g. an
                        individually-granted paralegal). */}
                    <Route path="/admin/hr-templates" element={<HRTemplates />} />
                    {/* Training Vault + Guided Tours launcher on its own admin-
                        layout mount so admins relaunch tours without leaving the
                        admin shell (#1328, D4-ONB-009; follows the #1153 /admin/
                        document-vault precedent — same shared component). */}
                    <Route path="/admin/training" element={<TrainingVault />} />
                  </Route>
                </Route>

                {/* CRM routes (all authenticated users) */}
                <Route element={<ProtectedRoute />}>
                  <Route element={<CrmLayout />}>
                    {/* #1267: back-office presets (and admins) are
                        redirected away from the agent dashboard to their own
                        landing; a plain field agent keeps it. */}
                    <Route element={<AgentDashboardRoute />}>
                      <Route path="/crm/dashboard" element={<CrmDashboard />} />
                    </Route>
                    {/* Contact Management (D2-CON-001 through D2-CON-011) */}
                    <Route path="/crm/contacts" element={<ContactList />} />
                    <Route path="/crm/contacts/:id" element={<ContactDetail />} />
                    {/* Farm / Area Management (D2-FRM-001 through D2-FRM-015) */}
                    <Route path="/crm/farm" element={<FarmList />} />
                    <Route path="/crm/farm/:id" element={<FarmDetail />} />
                    {/* LOOM canvassing (#3254). Deliberately NOT inside
                        CanvasserExcludedRoute: a canvasser works the farmbook
                        and canvasses their allocated agents' areas, exactly as
                        they do on /crm/farm. The server scopes every call
                        through farm_area_ids_for_user, so an unallocated
                        canvasser sees nothing here either way. */}
                    <Route path="/crm/canvassing" element={<Canvassing />} />

              {/* LOOM property lookup (#3273). Same audience and same
                  reasoning as /crm/canvassing above: a canvasser verifying a
                  registered owner before a door-knock is doing the job this
                  page exists for, and every call is server-scoped and
                  consent-gated identically. */}
              <Route
                path="/crm/property-lookup"
                element={<PropertyLookup />}
              />
                    {/* In-house referral workflow (D4-REF-001 .. 008) */}
                    <Route path="/crm/referrals" element={<Referrals />} />
                    {/* Property Listings (D2-LST-001 through D2-LST-016).
                        #2735: canvasser-excluded -- a canvasser is 403'd by
                        direct URL; plain agents + office-admin (list_properties)
                        keep access (nav already hides Listings for a canvasser). */}
                    <Route element={<CanvasserExcludedRoute />}>
                      {/* D4-FBK-4405: the listing book + farm-link queue are
                          not finance-desk surfaces -- redirected to the desk's
                          home. A listing DETAIL (linked from a deal file)
                          stays open. */}
                      <Route element={<FinanceDeskExcludedRoute />}>
                        <Route path="/crm/listings" element={<ListingList />} />
                        {/* #2877: farmbook erf-match review queue (before :id) */}
                        <Route
                          path="/crm/listings/farm-link-suggestions"
                          element={<FarmLinkSuggestions />}
                        />
                      </Route>
                      {/* D5-CAP-001: agent add-a-mandate front door (before :id) */}
                      <Route path="/crm/listings/add-mandate" element={<AddMandate />} />
                      <Route path="/crm/listings/:id" element={<ListingDetail />} />
                    </Route>
                    {/* Buyer Management (D2-BUY-001 through D2-BUY-015) */}
                    <Route path="/crm/buyers" element={<BuyerList />} />
                    <Route path="/crm/buyers/:id" element={<BuyerDetail />} />
                    {/* Deal Entry (D2-OTP-001 through D2-OTP-020) */}
                    <Route path="/crm/deals" element={<DealList />} />
                    {/* D5-CAP-001: agent add-a-sale front door (before :id) */}
                    <Route path="/crm/deals/add-sale" element={<AddSale />} />
                    <Route path="/crm/deals/:id" element={<DealDetail />} />
                    {/* Leases register (#3720, D2-OTP-026): every live lease and
                        its renewal lifecycle. Server-scoped (back office = whole
                        book, agent = own leases); canvasser-excluded like the
                        other deal surfaces. */}
                    <Route element={<CanvasserExcludedRoute />}>
                      <Route path="/crm/leases" element={<LeasesRegister />} />
                    </Route>
                    {/* FICA document matrix — required-but-missing flagging (D5-ASW-007) */}
                    {/* D4-FBK-4405: not a finance-desk surface. */}
                    <Route element={<FinanceDeskExcludedRoute />}>
                      <Route path="/crm/fica-matrix" element={<FicaMatrix />} />
                    </Route>
                    {/* Agent commission questionnaire (maker) — #452, D4-AUTH-002 */}
                    <Route
                      path="/crm/deals/:id/commission"
                      element={<CommissionQuestionnaireForm />}
                    />
                    {/* Document Vault — unified document repository (D4-DOV-001 /
                        D4-DOC-001). #2738: canvasser-excluded -- a canvasser is
                        403'd by direct URL; plain agents + office-admin keep it
                        (nav hides it for a canvasser via canvasserHidden). */}
                    <Route element={<CanvasserExcludedRoute />}>
                      {/* D4-FBK-4405: nor the finance desk (redirected home). */}
                      <Route element={<FinanceDeskExcludedRoute />}>
                        <Route path="/crm/document-vault" element={<DocumentVault />} />
                      </Route>
                    </Route>
                    {/* Training Vault (D4-TRN-001 through D4-TRN-006) */}
                    <Route path="/crm/training" element={<TrainingVault />} />
                    {/* Tasks & Reminders (D2-TSK-001 through D2-TSK-008) */}
                    <Route path="/crm/tasks" element={<TaskList />} />
                    <Route path="/crm/tasks/:id" element={<TaskDetail />} />
                    {/* Appointments / Agenda (D4-APT-001) */}
                    <Route path="/crm/appointments" element={<AppointmentsAgenda />} />
                    {/* Messaging (D2-MSG-001 through D2-MSG-010) */}
                    {/* D4-FBK-4405: not a finance-desk surface. */}
                    <Route element={<FinanceDeskExcludedRoute />}>
                      <Route path="/crm/messaging" element={<MessagingPage />} />
                    </Route>
                    {/* Agent Stats — My Commission, My Targets, Leaderboard.
                        D5-RBP-001 / #1051: these are money/value surfaces gated on
                        view_commission so the value-free back-office presets
                        (paralegal/manager/accounts) cannot reach their own figures
                        by direct URL. A standard agent + Finance are unaffected. */}
                    <Route element={<CommissionRoute />}>
                      {/* D4-FBK-4405: My Commission is an own-book page -- the
                          finance desk is redirected home. The leaderboard stays
                          (Finance reaches it under Legal & Finance, #2680). */}
                      <Route element={<FinanceDeskExcludedRoute />}>
                        <Route path="/crm/commission" element={<MyCommission />} />
                      </Route>
                      <Route path="/crm/leaderboard" element={<MyLeaderboard />} />
                    </Route>
                    <Route element={<SalesTargetsRoute />}>
                      <Route path="/crm/targets" element={<MyTargets />} />
                    </Route>
                    {/* KPI Activity Logging (#847). #2741: office-admin-excluded --
                        the agent KPI dashboard is a selling-agent surface, so an
                        office-admin is 403'd by direct URL (nav already hides it as
                        agentBookOnly); plain agents keep it. */}
                    <Route element={<OfficeAdminExcludedRoute />}>
                      {/* D4-FBK-4405: nor the finance desk (redirected home). */}
                      <Route element={<FinanceDeskExcludedRoute />}>
                        <Route path="/crm/kpis" element={<KPIActivityLogPage />} />
                      </Route>
                    </Route>
                    {/* KPI agent confirmation loop (WP6, #1789).
                        D4-FBK-4405: not a finance-desk surface. */}
                    <Route element={<FinanceDeskExcludedRoute />}>
                      <Route path="/crm/kpi-confirm" element={<KPIConfirmation />} />
                    </Route>
                    {/* Personal settings (self-service profile + password) — D2-USR-006 */}
                    <Route path="/crm/settings" element={<PersonalSettings />} />
                    {/* Agent self-service "My Documents" — D7-HRM-020, EPIC #2033 WP3 */}
                    <Route path="/crm/my-documents" element={<MyDocuments />} />
                    {/* Connect your AI (MCP) — D3-MCP / #361 */}
                    <Route path="/crm/connect" element={<ConnectAI />} />
                    {/* Feedback reports — running list (#547, D4-FBK-017) */}
                    <Route path="/crm/feedback/reports" element={<FeedbackReports />} />
                  </Route>
                </Route>

                {/* Gated landing SHELLS (#452, D4-AUTH-001): same CRM app, but
                    behind the financial module + the user's permission preset. */}
                <Route element={<ParalegalRoute />}>
                  <Route element={<CrmLayout />}>
                    {/* Paralegal deals-to-process workspace (D5-ASW-002): a
                        value-free, stage-filtered queue. */}
                    {/* D4-FBK-4405: the deals-to-process workspace and the
                        Legal Queue LIST are not finance-desk surfaces -- the
                        finance desk is redirected home. A deal file
                        (/legal/:otpId) stays open to it. */}
                    <Route element={<FinanceDeskExcludedRoute />}>
                      <Route
                        path="/paralegal"
                        element={<ParalegalWorkspace />}
                      />
                      <Route path="/legal" element={<LegalQueue />} />
                    </Route>
                    {/* File Cover Page milestone tracker (D4-DWF-009, #431) */}
                    <Route path="/legal/:otpId" element={<FileCoverPage />} />
                  </Route>
                </Route>
                <Route element={<FinanceRoute />}>
                  <Route element={<CrmLayout />}>
                    <Route path="/finance" element={<FinanceQueue />} />
                    {/* Two-person commission release inbox (D4-AUTH-004, #1254)
                        reachable by the Finance preset (stage 1 finance approval).
                        The same inbox is at /admin/commission-authorizations for
                        the super-user release (stage 2); the component offers only
                        the action the current user is entitled to. */}
                    <Route
                      path="/finance/authorizations"
                      element={<CommissionAuthorizationInbox />}
                    />
                  </Route>
                </Route>
                {/* Payslips -- on-demand commission payslip generation
                    (#984/#1014). Reachable by Finance (process_payment) inside
                    the CRM shell via a keep-in-shell mount of the SAME Payslips
                    component admins use at /admin/payslips (the #1153
                    /admin/document-vault, #1299 /crm/users precedent), so Finance
                    never leaves its shell and is not granted the admin role
                    (D5-RBP-003, #1413, EPIC #1410). The backend payslip endpoints
                    (CanGeneratePayslips) already authorise process_payment. */}
                <Route element={<PayslipsRoute />}>
                  <Route element={<CrmLayout />}>
                    <Route path="/crm/payslips" element={<Payslips />} />
                  </Route>
                </Route>
                {/* Commission Sheet -- the monthly Finance/Super-User commission
                    workbook (#2201). Mounted admin-shell-only at
                    /admin/commission-sheet; #2444 adds this Finance-reachable
                    keep-in-shell mount so the FINANCE / FINANCE_ACCOUNTS preset
                    (process_payment) can reach the SAME CommissionSheet component
                    without the admin role. The backend CanViewCommissionSheet
                    (process_payment, #2385) is the source of truth; a
                    commission-masked preset is 403 here and from the endpoint. */}
                <Route element={<CommissionSheetRoute />}>
                  <Route element={<CrmLayout />}>
                    <Route
                      path="/crm/commission-sheet"
                      element={<CommissionSheet />}
                    />
                  </Route>
                </Route>
                {/* Over-rider Schedules -- admin/finance self-service editing
                    of the flat banded per-deal rider (#2865). Mounted
                    admin-shell-only at /admin/overrider-schedules; this
                    Finance-reachable keep-in-shell mount (the #2444 pattern)
                    lets the FINANCE / FINANCE_ACCOUNTS preset (process_payment)
                    reach the SAME component without the admin role. The backend
                    OverriderScheduleViewSet (CanViewCommissionSheet, #2385) is
                    the source of truth. */}
                <Route element={<OverriderScheduleRoute />}>
                  <Route element={<CrmLayout />}>
                    <Route
                      path="/crm/overrider-schedules"
                      element={<OverriderSchedules />}
                    />
                  </Route>
                </Route>
                {/* Outstanding Deals register (#2463) -- the whole open book of
                    unregistered deals with commission-outstanding totals; the
                    in-app replacement for the OUTSTANDING DEALS workbook.
                    Mounted admin-shell at /admin/outstanding-deals; this
                    Finance-reachable keep-in-shell mount (the #2444 pattern)
                    lets the FINANCE / FINANCE_ACCOUNTS preset (process_payment)
                    reach the SAME component without the admin role. Backend
                    CanViewOutstandingDealsRegister / CanViewCommissionSheet is
                    the source of truth. */}
                <Route element={<OutstandingDealsRoute />}>
                  <Route element={<CrmLayout />}>
                    <Route
                      path="/crm/outstanding-deals"
                      element={<OutstandingDeals />}
                    />
                  </Route>
                </Route>
                {/* Market Share (#2680) -- the registered & written-sales board
                    (per-area deal counts + sale volumes, NO commission rand).
                    Mounted admin-shell at /admin/market-share and management-
                    shell at /crm/oversight/market-share; this Finance-reachable
                    keep-in-shell mount (the #2444 / #2463 pattern) lets the
                    FINANCE / FINANCE_ACCOUNTS preset (process_payment) reach the
                    SAME MarketShare component without the admin role. Backend
                    CanViewMarketShare (process_payment OR view_management_dashboards)
                    is the source of truth. */}
                <Route element={<MarketShareRoute />}>
                  <Route element={<CrmLayout />}>
                    <Route
                      path="/crm/market-share"
                      element={<MarketShare />}
                    />
                  </Route>
                </Route>
                {/* Bonds by Bank (#4323) -- the Finance-reachable mount of
                    the SAME BondAnalytics component (admin-shell
                    /admin/bond-analytics, management-shell
                    /crm/oversight/bond-analytics). Backend CanViewBondAnalytics
                    (process_payment OR view_management_dashboards) is the
                    source of truth. */}
                <Route element={<BondAnalyticsRoute />}>
                  <Route element={<CrmLayout />}>
                    <Route
                      path="/crm/bond-analytics"
                      element={<BondAnalytics />}
                    />
                  </Route>
                </Route>
                {/* Manager sign-off + Accounts invest surfaces (D5-RBP-001):
                    same CRM app, gated by the manager / accounts preset flags. */}
                <Route element={<ManagerRoute />}>
                  <Route element={<CrmLayout />}>
                    <Route
                      path="/management"
                      element={<ManagerSignoffQueue />}
                    />
                  </Route>
                </Route>
                {/* Back-compat (#1688): the Management workspace route was renamed
                    to /management to match its "Management" label. This shim keeps
                    old bookmarks AND the historical legacy Verify links on past
                    feedback issues working (none should 404). This route line is
                    the single allowed legacy reference outside tests (the
                    NoStaleManageSignoffRoute grep-guard excepts it). */}
                <Route
                  path="/manage-signoff"
                  element={<Navigate to="/management" replace />}
                />
                <Route element={<AccountsRoute />}>
                  <Route element={<CrmLayout />}>
                    <Route path="/accounts" element={<AccountsInvestQueue />} />
                  </Route>
                </Route>
                {/* #1506: Finance and Accounts are the SAME person at LWP -- a
                    single combined workspace that surfaces BOTH the commission
                    release (FinanceQueue) and deposit-to-invest
                    (AccountsInvestQueue) functions. Reachable by a Finance
                    (process_payment) OR Accounts (invest_funds) capability; the
                    /finance and /accounts routes above still work in their own
                    right (nothing regresses). */}
                <Route element={<FinanceAccountsRoute />}>
                  <Route element={<CrmLayout />}>
                    <Route
                      path="/finance-accounts"
                      element={<FinanceAccountsQueue />}
                    />
                  </Route>
                </Route>
                {/* FICA Outstanding -- payment readiness (#2265). Finance
                    (process_payment) OR management (view_management_dashboards)
                    read-only company view: deals with outstanding required FICA
                    docs flagged "not ready for payment". */}
                <Route element={<FinanceFicaOutstandingRoute />}>
                  <Route element={<CrmLayout />}>
                    <Route
                      path="/crm/finance/fica-outstanding"
                      element={<FinanceFicaOutstanding />}
                    />
                  </Route>
                </Route>
                {/* Deals Lodged cash-flow feed (#2263, role-matrix v2 A3):
                    Finance (view_all_fica) read-only company view of lodged
                    deals on the runway to registration + monies-received. */}
                <Route element={<FinanceDealsLodgedRoute />}>
                  <Route element={<CrmLayout />}>
                    <Route
                      path="/crm/finance/deals-lodged"
                      element={<FinanceDealsLodged />}
                    />
                  </Route>
                </Route>
                {/* Conveyancer directory (D2-CNV-001..003) -- back-office only
                    (#1025). Field agents do not need the conveyancer firm
                    directory; the guard requires capture_deal (admins + the
                    paralegal/accounts/finance/manager presets). Agents who reach
                    the URL get a 403 panel; the backend enforces it too. */}
                <Route element={<ConveyancerRoute />}>
                  <Route element={<CrmLayout />}>
                    <Route path="/crm/conveyancers" element={<ConveyancerList />} />
                    {/* Partner directory (D9-EXT-009): the capture_deal cohort
                        less the paralegal desk (#4157); admins use /admin/partners. */}
                    <Route element={<PartnersRoute />}>
                      <Route path="/crm/partners" element={<PartnersList />} />
                      <Route path="/crm/partners/:id" element={<PartnerDetail />} />
                    </Route>
                  </Route>
                </Route>
                {/* Templates -- letter/email template catalog (D2-DOC-001..004),
                    back-office only (#1227, D2-DOC-012). The buyer/seller/attorney/
                    lessee/mandate letters are prepared + sent by the paralegal
                    department, never a field agent; the guard requires capture_deal
                    (admins + the paralegal/accounts/finance/manager presets) and the
                    documents module. Agents who reach the URL get a 403 panel; the
                    backend (CanAccessTemplates) enforces it too. */}
                <Route element={<TemplatesRoute />}>
                  {/* D4-FBK-4405: the finance desk is redirected home. */}
                  <Route element={<FinanceDeskExcludedRoute />}>
                    <Route element={<CrmLayout />}>
                      <Route path="/crm/documents" element={<DocumentLibrary />} />
                    </Route>
                  </Route>
                </Route>
                {/* Scoped user management (#2847, ex WP8a #1299): admins reach
                    this via /admin/users in the admin shell; a non-admin
                    onboarding-rank user-manager (Manager / Paralegal / HR,
                    manage_users=True) reaches the SAME page here, inside the
                    CRM shell. The backend rank-hierarchy + no-escalation guard
                    is authoritative (strictly-lower-rank targets only; Finance/
                    Accounts + Office Admin have NO user management, #2840).
                    Reuses the UserManagement page; users without manage_users
                    get a 403. */}
                <Route element={<ManageUsersRoute />}>
                  <Route element={<CrmLayout />}>
                    <Route path="/crm/users" element={<UserManagement />} />
                  </Route>
                </Route>
                {/* Agent compliance roster (EPIC #2033 WP4 #2037, WP11 #2090,
                    #2381; re-scoped by #2847/#2842): the SAME roster serves HR
                    (manage_compliance -- owns the module) AND the read-only
                    manager oversight (view_management_dashboards, #1411) here
                    in the CRM shell -- the old manage_users clause (the
                    office-admin's WP4 access) is withdrawn. The guard mirrors
                    the nav item's anyFlag; the backend gates read vs write
                    (admins use /admin/compliance/agents). */}
                <Route element={<AgentComplianceRoute />}>
                  <Route element={<CrmLayout />}>
                    <Route
                      path="/crm/compliance/agents"
                      element={<AgentComplianceRoster />}
                    />
                  </Route>
                </Route>
                {/* Restricted HR / Personnel Document Templates (#2813): a
                    genuine PER-USER grant (manage_hr_documents), never a
                    preset -- "Only Natalie, Cam and optionally the paralegal"
                    per Natalie/Cam's 2026-07-29 ruling. A regular agent, and
                    every other back-office preset unless individually
                    granted, is 403'd here; admins reach the SAME page via
                    /admin/hr-templates above (superset). */}
                <Route element={<HRTemplatesRoute />}>
                  <Route element={<CrmLayout />}>
                    <Route path="/crm/hr-templates" element={<HRTemplates />} />
                  </Route>
                </Route>
                {/* Behind-schedule view (D5-ASW-005): paralegal + weekly
                    management meeting. Reachable with capture_deal OR
                    sign_off_deals; value-free. */}
                <Route element={<BehindScheduleRoute />}>
                  <Route element={<CrmLayout />}>
                    <Route
                      path="/behind-schedule"
                      element={<BehindScheduleView />}
                    />
                  </Route>
                </Route>
                {/* Import farm book (#3442): the OFFICE ADMIN
                    (list_properties) and the MANAGER (view_management_dashboards)
                    load farm books, and both live in the CRM shell (back-office
                    presets sit on role="agent"), so the admin page is mounted
                    keep-in-shell here — the #1153 /admin/document-vault, #1299
                    /crm/users precedent. CanImportFarmBook is the server-side
                    gate; a field agent / canvasser holds neither flag. */}
                <Route element={<FarmBookImportRoute />}>
                  <Route element={<CrmLayout />}>
                    <Route
                      path="/crm/farm-book-import"
                      element={<FarmBookImport />}
                    />
                  </Route>
                </Route>
                {/* Management oversight (#1411, EPIC #1410): a MANAGER
                    (view_management_dashboards) oversees the business READ-ONLY
                    via keep-in-shell mounts of the SAME admin dashboard
                    components (the #1153 /admin/document-vault, #1299 /crm/users
                    precedent) -- no admin role, no 403. Commission stays masked
                    (the flag is NOT view_commission; the backend keeps every
                    commission/payout endpoint on its own gate). The Leaderboard
                    + Sales Targets the manager sees live on the existing
                    CommissionRoute/SalesTargetsRoute mounts (now OR-gated on the
                    management flag), so they are not duplicated here. */}
                <Route element={<ManagementDashboardsRoute />}>
                  <Route element={<CrmLayout />}>
                    <Route
                      path="/crm/oversight/monthly-deals"
                      element={<MonthlyDeals />}
                    />
                    <Route
                      path="/crm/oversight/deal-pipeline"
                      element={<DealPipeline />}
                    />
                    {/* #2823 (split from #2817): ONE master filterable view of
                        the outstanding deal book -- category / agent / date
                        range -- instead of several separately-labelled fixed
                        buckets. Value-free; same data the #1851 work-category
                        queues already show a manager. */}
                    <Route
                      path="/crm/oversight/deal-queue"
                      element={<OversightDealQueue />}
                    />
                    <Route
                      path="/crm/oversight/pipeline-cashflow"
                      element={<PipelineCashflow />}
                    />
                    <Route
                      path="/crm/oversight/attorney-pipeline"
                      element={<AttorneyPipeline />}
                    />
                    <Route
                      path="/crm/oversight/compliance"
                      element={<FicaDashboard />}
                    />
                    {/* FICA Outstanding payment-readiness -- manager read-only
                        oversight (#2455). Same page component as the finance
                        /crm/finance/fica-outstanding surface; the manager reaches
                        it here under the view_management_dashboards mount. */}
                    <Route
                      path="/crm/oversight/fica-outstanding"
                      element={<FinanceFicaOutstanding />}
                    />
                    <Route
                      path="/crm/oversight/kpis"
                      element={<KPIReport />}
                    />
                    <Route
                      path="/crm/oversight/kpi-exceptions"
                      element={<KPIExceptions />}
                    />
                    <Route
                      path="/crm/oversight/kpi-scorecard"
                      element={<KPIMonthlyScorecard />}
                    />
                    <Route
                      path="/crm/oversight/communications"
                      element={<AdminCommunications />}
                    />
                    {/* KPI admin surfaces -- read-only manager VIEW access
                        (#2282). Editing/publishing/upload stay admin-only,
                        server-enforced. */}
                    <Route
                      path="/crm/oversight/kpi-metrics"
                      element={<KPIMetricEditor />}
                    />
                    <Route
                      path="/crm/oversight/kpi-tiers"
                      element={<KPITiers />}
                    />
                    <Route
                      path="/crm/oversight/kpi-upload"
                      element={<KPIUpload />}
                    />
                    {/* Market Share (#2272) -- read-only oversight, no commission rand. */}
                    <Route
                      path="/crm/oversight/market-share"
                      element={<MarketShare />}
                    />
                    {/* Activity Metrics + Agent Usage + Bonds by Bank (#2270). */}
                    <Route
                      path="/crm/oversight/activity"
                      element={<ActivityMetrics />}
                    />
                    <Route
                      path="/crm/oversight/agent-usage"
                      element={<AgentUsage />}
                    />
                    {/* Read-only per-agent oversight profile (#2728) --
                        drill-through from the Agent Usage roster. Commission
                        masked; RBAC + data-floor inherited from the endpoints. */}
                    <Route
                      path="/crm/oversight/agent/:id"
                      element={<AgentProfile />}
                    />
                    <Route
                      path="/crm/oversight/bond-analytics"
                      element={<BondAnalytics />}
                    />
                    {/* Areas master list (#2279) -- read-only for manager. */}
                    <Route
                      path="/crm/oversight/areas"
                      element={<AreaManagement />}
                    />
                    {/* Mandate/FICA checklist catalogue (#2282) -- read-only for manager. */}
                    <Route
                      path="/crm/oversight/checklist-templates"
                      element={<ChecklistTemplates />}
                    />
                  </Route>
                </Route>

                {/* Legacy routes -- redirect to new paths */}
                <Route path="/dashboard" element={<RoleRedirect />} />
                <Route path="/dashboard/*" element={<RoleRedirect />} />

                {/* Default redirect */}
                <Route path="*" element={<RoleRedirect />} />
              </Routes>
              </Suspense>
              </RouteErrorBoundary>
            </SupportChatProvider>
            </TourProvider>
            </PreviewPersonaProvider>
            </ToastProvider>
          </AuthProvider>
        </ModulesProvider>
  );
}

export default App;
